import {
  ErrorCode,
  EvaluationContext,
  JsonValue,
  Logger,
  Provider,
  ResolutionDetails,
  StandardResolutionReasons,
} from "@openfeature/web-sdk";

import { AllFeaturesQuery, FeatureStatus } from "../graphql/graphql";
import { getApolloClient } from "../graphql-client";
import { FEATURES } from "./queries";

export default class PerTherapistFeatureProvider implements Provider {
  public readonly runsOn = "client";
  readonly metadata = {
    name: "PerTherapistFeatureProvider",
  } as const;

  private featureStatuses: Record<string, FeatureStatus> = {};

  async initialize(_context: EvaluationContext) {
    this.featureStatuses = await this.getFeaturesForCache();
  }

  async onContextChange(_context: EvaluationContext) {
    // Context change is probably the user login state
    this.featureStatuses = await this.getFeaturesForCache();
  }

  resolveBooleanEvaluation(
    flagKey: string,
    defaultValue: boolean,
    _context: EvaluationContext,
    _logger: Logger
  ): ResolutionDetails<boolean> {
    const feature = this.featureStatuses[flagKey];
    if (feature === undefined) {
      return {
        value: defaultValue,
        reason: StandardResolutionReasons.ERROR,
        errorCode: ErrorCode.FLAG_NOT_FOUND,
        errorMessage: `Flag not available from provider: ${this.metadata.name}: ${flagKey}`,
      };
    }
    return {
      value: feature?.enabled,
      reason: StandardResolutionReasons.UNKNOWN,
    };
  }

  resolveStringEvaluation(
    flagKey: string,
    defaultValue: string,
    _context: EvaluationContext,
    _logger: Logger
  ): ResolutionDetails<string> {
    const feature = this.featureStatuses[flagKey];
    if (feature === undefined) {
      return {
        value: defaultValue,
        reason: StandardResolutionReasons.ERROR,
        errorCode: ErrorCode.FLAG_NOT_FOUND,
        errorMessage: `Flag not available from provider: ${this.metadata.name}: ${flagKey}`,
      };
    }
    return {
      value: feature?.treatment,
      reason: StandardResolutionReasons.UNKNOWN,
    };
  }

  resolveNumberEvaluation(
    _flagKey: string,
    _defaultValue: number,
    _context: EvaluationContext,
    _logger: Logger
  ): ResolutionDetails<number> {
    throw new Error("Method not implemented.");
  }

  resolveObjectEvaluation<T extends JsonValue>(
    _flagKey: string,
    _defaultValue: T,
    _context: EvaluationContext,
    _logger: Logger
  ): ResolutionDetails<T> {
    throw new Error("Method not implemented.");
  }

  private async getFeaturesForCache() {
    const client = getApolloClient();
    return (await client.query<AllFeaturesQuery>({ query: FEATURES })).data.features.reduce(
      (agg, status) => {
        return { ...agg, [status.featureName]: status };
      },
      {} as Record<string, FeatureStatus>
    );
  }
}
